import React, { FC, ReactNode, useContext } from 'react';

import CheckBoxTreeListTitle from '../CheckBoxTreeListTitle';
import { AccordionCard, AccordionCardHeader, AccordionCardTitle, AccordionChevronRight, H4GroupName } from './styles';
import {
  FilterType,
  TCheckBoxListValues,
  TCurrentFilter,
  TCurrentTextBox,
  TCurrentTextBoxIsLoadingState,
  TFilter,
  TGroupedFilter,
  TRangeValues
} from '../../../../commonTypes/filters';
import { TActiveQuickFilter } from '../../redux/types';
import CategoryFilter from './CategoryFilter';
import { AccordionContext, useAccordionToggle } from 'react-bootstrap';
import Accordion from 'react-bootstrap/Accordion';
import clsx from 'clsx';
import { Datepicker } from '../Datepicker';
import { TextBox } from '../TextBox';
import { CheckboxList } from '../CheckboxList';
import { NumericRange } from '../NumericRange';

interface Props {
  data: TGroupedFilter;
  currentFilterItems: TCurrentFilter[];
  currentTextBoxItems: TCurrentTextBox[];
  isLoadingStates: TCurrentTextBoxIsLoadingState[];
  onSetFilter: (data: TCurrentFilter) => void;
  onClearFilter: (data: string) => void;
  onClearCurrentTextBoxValues: (data: string[]) => void;
  onFetchDataTextBoxFilterAction: (internalName: string, searchValue: string) => void;
  onFetchCancel: (internalName: string) => void;
  onSetActiveQuickFilter: (data: TActiveQuickFilter) => void;
  activeQuickFilters?: TActiveQuickFilter;
  showColor?: boolean;
}

export const AccordionCardBlock: FC<Props> = ({
  data,
  currentFilterItems,
  currentTextBoxItems,
  isLoadingStates,
  onSetFilter,
  onClearFilter,
  onClearCurrentTextBoxValues,
  onFetchDataTextBoxFilterAction,
  onFetchCancel,
  onSetActiveQuickFilter,
  activeQuickFilters = null,
  showColor = false
}: Props) => {
  const ContextAwareToggle = ({
    children,
    eventKey,
    callback
  }: {
    children: string | ReactNode;
    eventKey: string;
    callback?: (key: string) => void;
  }) => {
    const currentEventKey = useContext(AccordionContext);
    const decoratedOnClick = useAccordionToggle(eventKey, () => callback && callback(eventKey));
    const isCurrentEventKey = currentEventKey === eventKey;

    return (
      <Accordion.Toggle
        eventKey={eventKey}
        as={AccordionCardTitle}
        onClick={decoratedOnClick}
        className={clsx({ ['is-collapsed']: isCurrentEventKey }, 'filter-card-title')}
      >
        {children}
        <AccordionChevronRight size={13} />
      </Accordion.Toggle>
    );
  };

  return (
    <AccordionCard className={clsx({ ['group-card']: !!data.group }, 'filter-card')}>
      {data.group && <H4GroupName>{data.group}</H4GroupName>}

      {data.items.map((item: TFilter, index) => {
        return item.isInnerCategory ? null : (
          <div key={`${item.internalName}-${index}`}>
            <AccordionCardHeader className="filter-card-header">
              <ContextAwareToggle eventKey={item.internalName}>
                {item.filterTypeName === FilterType.CheckBoxTreeList ? (
                  <CheckBoxTreeListTitle
                    internalName={item.internalName}
                    label={item.displayName}
                    defaultValues={item.defaultValues as TCheckBoxListValues}
                    currentFilterItems={currentFilterItems}
                    onSetFilter={onSetFilter}
                    onClearFilter={onClearFilter}
                  />
                ) : (
                  item.displayName
                )}
              </ContextAwareToggle>
            </AccordionCardHeader>
            <Accordion.Collapse eventKey={item.internalName}>
              <>
                {item.filterTypeName === FilterType.CategoryFilter && (
                  <CategoryFilter
                    data={item}
                    currentFilterItems={currentFilterItems}
                    onSetFilter={onSetFilter}
                    onClearFilter={onClearFilter}
                  />
                )}
                {item.filterTypeName === FilterType.Date && (
                  <Datepicker
                    name={item.internalName}
                    currentFilterItems={currentFilterItems}
                    onSetFilter={onSetFilter}
                    onClearFilter={onClearFilter}
                  />
                )}
                {item.filterTypeName === FilterType.TextBox && (
                  <TextBox
                    name={item.internalName}
                    currentFilterItems={currentFilterItems}
                    currentTextBoxItems={currentTextBoxItems}
                    isLoadingStates={isLoadingStates}
                    onSetFilter={onSetFilter}
                    onClearFilter={onClearFilter}
                    onClearCurrentTextBoxValues={onClearCurrentTextBoxValues}
                    onFetchDataTextBoxFilterAction={onFetchDataTextBoxFilterAction}
                    onFetchCancel={onFetchCancel}
                    onSetActiveQuickFilter={onSetActiveQuickFilter}
                    activeQuickFilters={activeQuickFilters}
                    showColor={showColor}
                  />
                )}
                {item.filterTypeName === FilterType.CheckBoxList && (
                  <CheckboxList
                    name={item.internalName}
                    value={item.defaultValues as TCheckBoxListValues}
                    currentFilterItems={currentFilterItems}
                    onSetFilter={onSetFilter}
                    onClearFilter={onClearFilter}
                  />
                )}
                {item.filterTypeName === FilterType.CheckBoxTreeList && (
                  <CheckboxList
                    name={item.internalName}
                    value={item.defaultValues as TCheckBoxListValues}
                    currentFilterItems={currentFilterItems}
                    onSetFilter={onSetFilter}
                    onClearFilter={onClearFilter}
                  />
                )}
                {item.filterTypeName === FilterType.NumericRange && (
                  <NumericRange
                    name={item.internalName}
                    values={item.defaultValues as TRangeValues}
                    currentFilterItems={currentFilterItems}
                    onSetFilter={onSetFilter}
                    onClearFilter={onClearFilter}
                  />
                )}
                {item.filterTypeName === FilterType.MoneyRange && (
                  <NumericRange
                    name={item.internalName}
                    fixed={2}
                    prefix="$"
                    values={item.defaultValues as TRangeValues}
                    currentFilterItems={currentFilterItems}
                    onSetFilter={onSetFilter}
                    onClearFilter={onClearFilter}
                  />
                )}
              </>
            </Accordion.Collapse>
          </div>
        );
      })}
    </AccordionCard>
  );
};
